import { Slot } from "@radix-ui/react-slot";
import { isNotNil } from "es-toolkit";
import * as React from "react";
import { type ComponentProps, useCallback } from "react";
import { Button } from "../components/button";
import { Progress } from "../components/progress";
import { cn } from "../lib/utils";

interface StepContextValue {
  activeStep: number;
  setActiveStep: (step: number) => void;
  totalSteps: number;
  direction: "forward" | "backward";
  isFirstMount: boolean;
  next: () => void;
  previous: () => void;
}

const StepContext = React.createContext<StepContextValue | undefined>(undefined);

export function useStep() {
  const context = React.useContext(StepContext);
  if (!context) {
    throw new Error("Step components must be used within a StepGroup");
  }
  return context;
}

interface StepGroupProps {
  defaultStep?: number;
  onStepChange?: (step: number) => void;
  children: React.ReactNode;
}

const StepGroup = ({ defaultStep = 1, onStepChange, children }: StepGroupProps) => {
  const [activeStep, setActiveStep] = React.useState(defaultStep);
  const [direction, setDirection] = React.useState<"forward" | "backward">("forward");
  const [isFirstMount, setIsFirstMount] = React.useState(true);

  const totalSteps = (
    React.Children.map(children, (child) => {
      return React.isValidElement(child) && child.type === Step ? child : null;
    }) ?? []
  ).filter(isNotNil).length;

  const handleStepChange = React.useCallback(
    (step: number) => {
      setDirection(step > activeStep ? "forward" : "backward");
      setActiveStep(step);
      setIsFirstMount(false);
      onStepChange?.(step);
    },
    [activeStep, onStepChange],
  );

  const next = useCallback(() => {
    setActiveStep(Math.min(totalSteps, activeStep + 1));
  }, [activeStep, totalSteps]);

  const previous = useCallback(() => {
    setActiveStep(Math.max(1, activeStep - 1));
  }, [activeStep]);

  return (
    <StepContext.Provider
      value={{
        activeStep,
        setActiveStep: handleStepChange,
        totalSteps,
        direction,
        isFirstMount,
        next,
        previous,
      }}
    >
      {children}
    </StepContext.Provider>
  );
};
StepGroup.displayName = "StepGroup";

interface StepProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number;
}

const Step = React.forwardRef<HTMLDivElement, StepProps>(({ value, className, children, ...props }, ref) => {
  const { activeStep, direction, isFirstMount } = useStep();
  const isActive = activeStep === value;

  if (!isActive) return null;

  return (
    <div
      ref={ref}
      className={cn(
        "h-full",
        "animate-in",
        "duration-300",
        "ease-&lsqb;cubic-bezier(0.33,0.00,0.00,1.00)&rsqb",
        isFirstMount
          ? "fade-in-0 scale-100"
          : direction === "forward"
            ? "slide-in-from-right-8 fade-in-0"
            : "slide-in-from-left-8 fade-in-0",
        "transform-gpu",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
});
Step.displayName = "Step";

interface StepControlProps extends React.HTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  disabled?: boolean;
}

const StepPrevious = React.forwardRef<HTMLButtonElement, StepControlProps>(
  ({ asChild, className, children, ...props }, ref) => {
    const { activeStep, setActiveStep } = useStep();
    const Comp = asChild ? Slot : Button;

    return (
      <Comp
        ref={ref}
        type="button"
        variant="ghost"
        onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
        disabled={activeStep === 1 || props.disabled}
        className={cn("p-0", className)}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
StepPrevious.displayName = "StepPrevious";

const StepNext = React.forwardRef<HTMLButtonElement, StepControlProps>(
  ({ asChild, className, children, onClick, ...props }, ref) => {
    const { activeStep, setActiveStep, totalSteps } = useStep();
    const Comp = asChild ? Slot : Button;

    return (
      <Comp
        ref={ref}
        type="button"
        onClick={(e) => {
          onClick?.(e);
          setActiveStep(Math.min(totalSteps, activeStep + 1));
        }}
        disabled={activeStep === totalSteps}
        className={cn("", className)}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
StepNext.displayName = "StepNext";

function StepProgress(props: ComponentProps<typeof Progress>) {
  const { activeStep, totalSteps } = useStep();

  return <Progress value={(activeStep / totalSteps) * 100} {...props} />;
}

export { StepGroup, Step, StepPrevious, StepNext, StepProgress };
